/**
 * DateFilter Component
 * Simple date navigation for delivery users
 * Shows current date with prev/next buttons and date picker
 */

import React from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

interface DateFilterProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
}

export const DateFilter: React.FC<DateFilterProps> = ({ selectedDate, onDateChange }) => {
  // Format date for display
  const formatDisplayDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const isToday = date.toDateString() === today.toDateString();
      const isYesterday = date.toDateString() === yesterday.toDateString();
      const isTomorrow = date.toDateString() === tomorrow.toDateString();
      
      if (isToday) return 'Today';
      if (isYesterday) return 'Yesterday';
      if (isTomorrow) return 'Tomorrow';
      
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  
  const goToPreviousDay = () => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() - 1);
    onDateChange(current.toISOString().split('T')[0]);
  };
  
  const goToNextDay = () => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + 1);
    onDateChange(current.toISOString().split('T')[0]);
  };
  
  const goToToday = () => {
    onDateChange(new Date().toISOString().split('T')[0]);
  };
  
  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onDateChange(e.target.value);
  };
  
  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  return (
    <div className="w-full bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
      {/* Main Row: Arrows | Date Picker | Display Date | Today Button */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        
        {/* Navigation Buttons + Date Picker - Always visible */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
          {/* Previous Day */}
          <button
            onClick={goToPreviousDay}
            className="p-2.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors border border-gray-200"
            title="Previous day"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          {/* Date Picker - Full width on mobile */}
          <div className="relative flex items-center flex-1 sm:flex-none">
            <Calendar className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={selectedDate}
              onChange={handleDatePickerChange}
              className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-[160px]"
            />
          </div>
          
          {/* Next Day */}
          <button
            onClick={goToNextDay}
            className="p-2.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors border border-gray-200"
            title="Next day"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        {/* Display Date + Today Button */}
        <div className="flex items-center gap-3 w-full sm:w-auto justify-center sm:justify-start">
          {/* Display Date Text */}
          <div className="text-base sm:text-lg font-semibold text-gray-900">
            {formatDisplayDate(selectedDate)}
          </div>
          
          {/* Today Button */}
          {!isToday && (
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors whitespace-nowrap"
            >
              Today
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

DateFilter.displayName = 'DateFilter';
